import type { RoleList } from './roles/permissions';

/**
 * Which trades a member's INDIVIDUAL licence requirements are filtered by.
 *
 * - The member's own `profiles.specializations`, when set.
 * - Otherwise, for a company OWNER only, the company's `trade_companies.specializations`:
 *   the owner is the tradesperson (most are sole operators), so an empty own list must
 *   not read as "nothing required".
 * - Otherwise nothing. Employees are deliberately NOT given the company's trades — an
 *   apprentice or office worker would be told they need a full trade licence. Their
 *   owner is prompted to set their trade instead.
 *
 * One rule for every client screen that shows a member their licences (mobile own +
 * team views, web dashboard banner, web documents page).
 */
export type ComplianceTradeSource = 'own' | 'company' | 'none';

export interface ResolvedComplianceTrades {
  trades: string[];
  source: ComplianceTradeSource;
}

const OWNER_ROLE_NAMES = new Set(['TRADE_OWNER', 'OWNER']);

/** True when any of the member's roles (either shape) is an owner role. */
export function isTradeOwner(roles: RoleList, primaryRole?: string | null): boolean {
  if (primaryRole && OWNER_ROLE_NAMES.has(primaryRole)) return true;
  if (!Array.isArray(roles)) return false;
  return roles.some((r) => {
    const name = typeof r === 'string' ? r : r?.role_name;
    return typeof name === 'string' && OWNER_ROLE_NAMES.has(name);
  });
}

export function resolveComplianceTrades(
  ownSpecializations: string[] | null | undefined,
  companySpecializations: string[] | null | undefined,
  isOwner: boolean,
): ResolvedComplianceTrades {
  const own = (ownSpecializations ?? []).filter(Boolean);
  if (own.length > 0) return { trades: own, source: 'own' };
  const company = (companySpecializations ?? []).filter(Boolean);
  if (isOwner && company.length > 0) return { trades: company, source: 'company' };
  return { trades: [], source: 'none' };
}
